import { useQueryClient } from 'react-query';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { HostedFieldFieldOptions, hostedFields, HostedFields } from 'braintree-web';
import { useTheme } from '@mui/material';
import { EmptyFunction } from '~/adapters/typescript/emptyFunction';
import { useBraintreeClient } from '~/adapters/braintree/context';
import { useThrowToErrorBoundary } from '~/components/ErrorBoundary';
import {
    ErrorFields,
    HostedFieldError,
    isUnknownBraintreeError,
    normalizeError,
} from '~/adapters/braintree/errorHandler';
import { HostedFieldKeys } from '~/adapters/braintree/constants';
import { useNotifications } from '~/contexts/NotificationContext';
import { sendCardResultEvent } from './sendTrackingEvents';

type BraintreeHostedFieldToId = {
    [K in keyof HostedFieldFieldOptions]: string;
};

export const BraintreeToId: BraintreeHostedFieldToId = {
    cvv: 'bt-cvv',
    cardholderName: 'bt-card-holder-name',
    number: 'bt-card-number',
    expirationDate: 'bt-card-expiration',
    postalCode: 'bt-postal-code',
};

export const BraintreeToLabel: BraintreeHostedFieldToId = {
    cvv: 'CVV (3 or 4 digits)',
    cardholderName: 'Name on Card',
    number: 'Card Number',
    expirationDate: 'Expiration Date',
    postalCode: 'Zip Code',
};

interface HostedFieldContextType {
    submit: EmptyFunction;
    errorFields: ErrorFields;
    saving: boolean;
}

const HostedFieldContext = createContext<HostedFieldContextType | null>(null);

export function useHostedFields() {
    const context = useContext(HostedFieldContext);
    if (!context) {
        throw new Error('Hosted field not provided through context');
    }

    return context;
}

/**
 * creates hosted field options structure which looks like this
 * {
 *   number: {
 *     container: '#card-number',
 *     placeholder: '4111 1111 1111 1111'
 *   },
 *   cvv: {
 *     container: '#cvv',
 *     placeholder: '123'
 *   },
 *   expirationDate: {
 *     container: '#expiration-date',
 *     placeholder: '10/2022'
 *   }
 * }
 */
function toBraintreeHostedFieldOptions(acc: Partial<HostedFieldFieldOptions>, curr: keyof HostedFieldFieldOptions) {
    return { ...acc, [curr]: { container: `#${BraintreeToId[curr]}` } };
}

interface HostedFieldProps {
    onCardAdd?: () => void;
}

export function HostedFieldsProvider({ children, onCardAdd }: PropsWithChildren<HostedFieldProps>) {
    const queryClient = useQueryClient();
    const [saving, setSaving] = useState(false);
    const bclient = useBraintreeClient();
    const [hf, setHf] = useState<HostedFields | null>(null);
    const [errorFields, setErrorFields] = useState<ErrorFields>(new Set());
    const throwToErrorBoundary = useThrowToErrorBoundary();
    const theme = useTheme();
    const { pushSuccessNotification } = useNotifications();
    useEffect(() => {
        let fields: HostedFields;
        (async () => {
            try {
                fields = await hostedFields.create({
                    client: bclient,
                    styles: {
                        input: {
                            'font-size': `${theme.typography.fontSize}px`,
                            color: theme.palette.text.primary,
                        },
                    },
                    fields: HostedFieldKeys.reduce(toBraintreeHostedFieldOptions, {}),
                });
            } catch (e) {
                throwToErrorBoundary(e);
                return;
            }

            fields.on('validityChange', (ev) => {
                const field = ev.fields[ev.emittedBy];

                setErrorFields((errors) => {
                    if (!isUnknownBraintreeError(errors) && errors.has(ev.emittedBy) && field.isPotentiallyValid) {
                        const newErrorFields = new Set(errors);
                        newErrorFields.delete(ev.emittedBy);
                        return newErrorFields;
                    }
                    return errors;
                });
            });
            setHf(fields);
        })();
        return () => {
            fields?.teardown();
        };
    }, []);
    const submit = useCallback(async () => {
        if (!hf) {
            throw new Error('Hosted fields are not initialized correctly');
        }
        try {
            setSaving(true);
            await hf.tokenize({
                vault: true,
            });
            HostedFieldKeys.forEach((field) => hf.clear(field));
            await queryClient.invalidateQueries(['braintree-cards']);
            pushSuccessNotification('Your card has been saved.');
            setSaving(false);
            sendCardResultEvent('Add Card Success');
            if (onCardAdd) {
                onCardAdd();
            }
        } catch (e) {
            setSaving(false);
            const castedError: HostedFieldError = e as any;
            sendCardResultEvent('Add Card Failed');
            setErrorFields(normalizeError(castedError));
        }
    }, [hf]);

    const providerValue = useMemo(() => ({ submit, errorFields, saving }), [submit, errorFields, saving]);

    return <HostedFieldContext.Provider value={providerValue}>{children}</HostedFieldContext.Provider>;
}
